export interface KeplerResult {
  E: number;
  converged: boolean;
}
export interface OrbitState {
  a: number;
  e: number;
  i: number;
  omega: number;
  node: number;
  M: number;
}

export function createOrbitState(): OrbitState {
  return { a: 0, e: 0, i: 0, omega: 0, node: 0, M: 0 };
}
export function createKeplerResult(): KeplerResult {
  return { E: 0, converged: false };
}

const TOLERANCE = 1e-8;
const MAX_ITERATIONS = 30;

/** Solves E - e sin E = M by Newton iteration. Angles in radians. */
export function solveKepler(M: number, e: number, out: KeplerResult): KeplerResult {
  let E = e < 0.8 ? M : Math.PI;
  out.converged = false;
  for (let k = 0; k < MAX_ITERATIONS; k++) {
    const f = E - e * Math.sin(E) - M;
    const dE = f / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < TOLERANCE) {
      out.converged = true;
      break;
    }
  }
  out.E = E;
  return out;
}

/**
 * Writes the orbit plane's unit axes in the ecliptic frame: P towards periapsis into out[0..2],
 * Q a quarter turn ahead in the direction of motion into out[3..5].
 */
export function orbitBasis(s: OrbitState, out: Float64Array): void {
  const cw = Math.cos(s.omega),
    sw = Math.sin(s.omega);
  const cO = Math.cos(s.node),
    sO = Math.sin(s.node);
  const ci = Math.cos(s.i),
    si = Math.sin(s.i);
  // Standard rotation Rz(node) * Rx(i) * Rz(omega) applied to the x and y axes.
  out[0] = cO * cw - sO * ci * sw;
  out[1] = sO * cw + cO * ci * sw;
  out[2] = si * sw;
  out[3] = -cO * sw - sO * ci * cw;
  out[4] = -sO * sw + cO * ci * cw;
  out[5] = si * cw;
}

const basis = new Float64Array(6);

/** Writes the position (km) for eccentric anomaly E into out[offset..offset+2]. */
export function positionFromState(
  s: OrbitState,
  E: number,
  out: Float64Array,
  offset: number,
): void {
  orbitBasis(s, basis);
  const xp = s.a * (Math.cos(E) - s.e);
  const yp = s.a * Math.sqrt(1 - s.e * s.e) * Math.sin(E);
  out[offset] = xp * basis[0] + yp * basis[3];
  out[offset + 1] = xp * basis[1] + yp * basis[4];
  out[offset + 2] = xp * basis[2] + yp * basis[5];
}
